import { useContext, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { nanoid } from "nanoid";
import { useDispatch } from "react-redux";
import axios from "axios";
import { axiosPost } from "@/services/axiosService";
import { userSlice } from "@/redux/User";
import { BotContext } from "../store";

type UseDocumentUploadTrainingOptions = {
  characterLimit: number;
  characterUsed: number;
  isOld: boolean;
  plan: any;
  restartPolling?: () => void;
  setProcessingDocuments?: Dispatch<SetStateAction<string[]>>;
  setFileUsed?: Dispatch<SetStateAction<number>>;
  handleVideoUpload?: (videoFiles: File[]) => Promise<string[]>;
};

export function useDocumentUploadTraining({
  characterLimit,
  characterUsed,
  isOld,
  plan,
  restartPolling,
  setProcessingDocuments,
  setFileUsed,
  handleVideoUpload,
}: UseDocumentUploadTrainingOptions) {
  const dispatch = useDispatch();
  const [store, storeDispatch] = useContext(BotContext);
  const [uploadProgress, setUploadProgress] = useState<number>(264);
  const [showProgress, setShowProgress] = useState<boolean>(false);
  const [fileLoader, setFileLoader] = useState<boolean>(false);
  // Names what is uploading right now, so a multi-type run doesn't look stalled
  // while it works through one kind of file.
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const trainingDataRef = useRef<any[]>([]);
  const bufferDocsDataRef = useRef<any>([]);

  const resetBufferData = () => {
    bufferDocsDataRef.current = [];
    delete bufferDocsDataRef.current.failed;
  };

  const updateGlobalDataSources = (newData: any[], source: string) => {
    const existingData = { ...store.botData.dataSources };
    if (!existingData[source]) existingData[source] = [];
    existingData[source] = [...existingData[source], ...newData];
    storeDispatch({ type: "data-sources-update", dataSources: existingData });
    setFileUsed?.(
      (prev) => prev + (newData?.reduce((acc, obj) => acc + obj.size, 0) || 0),
    );
  };

  const handleFileUploadWithPresignedUrl = async (
    files: File[],
    fileType: "pdf" | "xlsx" = "pdf",
    deferRows = false,
    // Slice of the overall bar this batch owns. Defaults to the full sweep for
    // single-type runs; a mixed run gives each batch its own band so the number
    // only ever climbs instead of restarting per batch.
    range: { from: number; to: number } = { from: 30, to: 80 },
  ) => {
    if (files.length === 0) return { successful: [], failed: [], rows: [] };

    // setUploadStatus(
    //   `Uploading ${files.length} file${files.length > 1 ? "s" : ""}...`,
    // );

    // Under deferRows the caller owns the progress bar across both batches.
    if (!deferRows) {
      setShowProgress(true);
      setUploadProgress(5);
      setFileLoader(true);
    }

    const successfulUploads: any[] = [];
    const failedUploads: any[] = [];
    let pdfRows: any[] = [];
    const totalFiles = files.length;
    const span = range.to - range.from;

    try {
      if (!deferRows) setUploadProgress(10);

      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file, file.name);
      });

      formData.append("botCustomId", store.botData.botCustomId);
      formData.append("botCreatedAt", store.botData.createdAt);
      formData.append("companyId", store.botData.companyId);
      formData.append("userId", store.botData.userId);

      setUploadProgress(1);

      const presignedUrlResponse: any = await axiosPost(
        "/ai/getPresignedUploadUrl",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!presignedUrlResponse.data?.success) {
        throw new Error(
          presignedUrlResponse.data?.message || "Failed to get presigned URLs",
        );
      }

      setUploadProgress(range.from);

      const uploadResults = presignedUrlResponse.data.data.uploadResults || [];
      if (uploadResults.length !== files.length) {
        throw new Error(
          "Mismatch between files sent and presigned URLs received",
        );
      }

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadData = uploadResults[i];

        if (!uploadData.success) {
          failedUploads.push({
            name: file.name,
            error: uploadData.error,
          });
          continue;
        }

        try {
          const baseProgress = range.from + (i / totalFiles) * span;
          setUploadProgress(baseProgress);

          const uploadResponse = await fetch(uploadData.uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type":
                file.type ||
                (fileType === "xlsx"
                  ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  : "application/pdf"),
            },
          });

          if (!uploadResponse.ok) {
            throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
          }

          setUploadProgress(baseProgress + span / totalFiles);

          const trainingUpdateResponse = await axiosPost(
            "/ai/pdf-upload-complete",
            {
              fileName: file.name,
              fileKey: uploadData.fileKey,
              fileSize: uploadData.fileSize,
              characterCount: uploadData.characterCount,
              botCustomId: store.botData.botCustomId,
              botCreatedAt: store.botData.createdAt,
            },
          );

          if (!trainingUpdateResponse.data.success) {
            throw new Error("Failed to update training data");
          }

          const newDocumentId = trainingUpdateResponse.data.data.documentId;
          successfulUploads.push({
            id: newDocumentId,
            name: file.name,
            size: uploadData.fileSize,
            fileKey: uploadData.fileKey,
            characterCount: uploadData.characterCount,
            status: "In Progress",
          });

          setProcessingDocuments?.((prev) => [...prev, newDocumentId]);
        } catch (error: any) {
          failedUploads.push({
            name: file.name,
            error: error.message,
          });
        }
      }

      // Only the final batch may show 100 — otherwise the bar completes once
      // per batch instead of once per run.
      if (!deferRows) setUploadProgress(100);

      pdfRows = successfulUploads.map((upload) => ({
        id: upload.id,
        title: upload.name,
        mime: "application/pdf",
        size: upload.size,
        status: upload.status,
        lastSyncTime: Date.now(),
        characterCount: 0,
        isPdfAsync: true,
      }));

      // When the caller is also uploading non-PDFs it commits both sets in one
      // dispatch, so the trained list doesn't render PDFs first and everything
      // else seconds later. Standalone callers still dispatch here.
      if (pdfRows.length > 0 && !deferRows) {
        // Append via the reducer rather than merging a `store` snapshot: both
        // upload paths capture the same pre-upload documents array, so a
        // replace here wiped whichever batch finished first.
        storeDispatch({
          type: "add-aiSource",
          dataKey: "documents",
          dataSet: pdfRows,
        });

        restartPolling?.();
      }

      if (failedUploads.length > 0) {
        dispatch(
          userSlice.actions.setToastPopUp({
            show: true,
            toastType: "error",
            message: `Failed to upload ${failedUploads.length} file(s): ${failedUploads.map((f) => f.name).join(", ")}`,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            error.response?.data?.message ||
            "Failed to upload PDF files. Please try again.",
        }),
      );
      return { successful: [], failed: failedUploads, rows: [] };
    } finally {
      // deferRows means another upload batch runs straight after this one, so
      // the caller owns hiding the loader once everything is done.
      if (!deferRows) {
        setTimeout(() => {
          setShowProgress(false);
          setFileLoader(false);
          setUploadStatus("");
        }, 500);
      }
    }

    return {
      successful: successfulUploads,
      failed: failedUploads,
      rows: pdfRows,
    };
  };

  const handleDocumentUploadWithPresignedUrl = async (
    files: File[],
    deferRows = false,
    range: { from: number; to: number } = { from: 30, to: 80 },
  ) => {
    if (files.length === 0) return { successful: [], failed: [], rows: [] };

    // Label by the extensions actually present — this batch mixes CSV, XLSX,
    // DOCX, TXT and MD.
    const extensions = Array.from(
      new Set(
        files
          .map((file) => file.name.split(".").pop()?.toUpperCase())
          .filter(Boolean),
      ),
    );
    setUploadStatus(
      `Uploading ${files.length} ${extensions.join(", ")} file${
        files.length > 1 ? "s" : ""
      }...`,
    );

    // Under deferRows the caller already opened the progress bar for the whole
    // run; re-opening it here restarted it from 1 and read as a second upload.
    if (!deferRows) {
      setShowProgress(true);
      setFileLoader(true);
      setUploadProgress(1);
    }

    const successfulUploads: any[] = [];
    const failedUploads: any[] = [];
    let docRows: any[] = [];
    const span = range.to - range.from;

    try {
      // 1. Ask for presigned URLs — metadata only, no file bytes leave the browser yet.
      const presignedResponse: any = await axiosPost(
        "/ai/getDocumentUploadUrl",
        {
          botCustomId: store.botData.botCustomId,
          configId: (store.botData as any)?.aiBotConfig?.configId,
          files: files.map((file) => ({
            fileName: file.name,
            fileSize: file.size,
            mime: file.type,
          })),
        },
      );

      if (!presignedResponse.data?.success) {
        throw new Error(
          presignedResponse.data?.message || "Failed to get upload URLs",
        );
      }

      setUploadProgress(range.from);

      const uploadResults = presignedResponse.data.data.uploadResults || [];
      const uploadedFiles: any[] = [];

      // 2. PUT each file directly to S3.
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadData =
          uploadResults.find((r: any) => r.fileName === file.name) ??
          uploadResults[i];

        if (!uploadData?.success) {
          failedUploads.push({
            name: file.name,
            error: uploadData?.error ?? "No upload URL returned",
          });
          continue;
        }

        try {
          const baseProgress = range.from + (i / files.length) * span;
          setUploadProgress(baseProgress);

          const uploadResponse = await fetch(uploadData.uploadUrl, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": uploadData.mime },
          });

          if (!uploadResponse.ok) {
            throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
          }

          uploadedFiles.push({
            fileName: file.name,
            fileKey: uploadData.fileKey,
            fileSize: uploadData.fileSize,
            mime: uploadData.mime,
          });

          setUploadProgress(baseProgress + span / files.length);
        } catch (error: any) {
          failedUploads.push({ name: file.name, error: error.message });
        }
      }

      if (uploadedFiles.length > 0) {
        const completeResponse: any = await axiosPost(
          "/ai/document-upload-complete",
          {
            botCustomId: store.botData.botCustomId,
            botCreatedAt: store.botData.createdAt,
            files: uploadedFiles,
          },
        );

        const idByFileName = new Map<string, string>(
          (completeResponse.data?.data?.documents || []).map((doc: any) => [
            doc.fileName,
            doc.documentId,
          ]),
        );

        const newDocuments = uploadedFiles.map((upload) => ({
          id: idByFileName.get(upload.fileName),
          title: upload.fileName,
          mime: upload.mime,
          size: upload.fileSize,
          status: "In Progress",
          lastSyncTime: Date.now(),
          characterCount: 0,
          isDocAsync: true,
        }));

        docRows = newDocuments;

        // Append via the reducer instead of merging a `store` snapshot: both
        // upload paths capture the same pre-upload snapshot, so a replace here
        // wiped whichever batch finished first. When the caller is committing
        // both sets together it passes deferRows and dispatches once, so the
        // trained list shows every file at the same time.
        if (!deferRows) {
          storeDispatch({
            type: "add-aiSource",
            dataKey: "documents",
            dataSet: newDocuments,
          });
        }

        setProcessingDocuments?.((prev) => [
          ...prev,
          ...newDocuments
            .map((doc) => doc.id)
            .filter((id): id is string => Boolean(id)),
        ]);

        successfulUploads.push(...uploadedFiles);
        restartPolling?.();
      }

      // Only the caller declares the run finished in a mixed upload.
      if (!deferRows) setUploadProgress(100);

      if (failedUploads.length > 0) {
        dispatch(
          userSlice.actions.setToastPopUp({
            show: true,
            toastType: "error",
            message: `Failed to upload ${failedUploads.length} file(s): ${failedUploads
              .map((f) => f.name)
              .join(", ")}`,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            error.response?.data?.message ||
            "Failed to upload documents. Please try again.",
        }),
      );
      return { successful: [], failed: failedUploads, rows: [] };
    } finally {
      // deferRows means the caller commits the rows and hides the loader once
      // every batch has finished.
      if (!deferRows) {
        setTimeout(() => {
          setShowProgress(false);
          setFileLoader(false);
          setUploadStatus("");
        }, 500);
      }
    }

    return {
      successful: successfulUploads,
      failed: failedUploads,
      rows: docRows,
    };
  };

  const extractTextFromDocument = async (
    extraFiles: File[],
    bufferFlag: boolean = false,
  ) => {
    if (extraFiles.length === 0) return;

    try {
      const formData: any = new FormData();
      for (const file of extraFiles) {
        formData.append("file", file, file.name);
      }

      formData.append("botCustomId", store.botData.botCustomId as string);
      formData.append("botCreatedAt", store.botData.createdAt as string);
      setFileLoader(true);

      const response: any = await axiosPost("/ai/extract-text", formData);
      const filteredData =
        response.data?.data?.filter((item) => item.characterCount !== 0) ?? [];

      if ((response.data?.data?.length ?? 0) !== filteredData.length) {
        dispatch(
          userSlice.actions.setToastPopUp({
            show: true,
            toastType: "error",
            message:
              filteredData.length === 0
                ? "The file was not processed due to zero character count."
                : "Some files were not processed due to zero character count.",
          }),
        );
      }

      if (bufferFlag) {
        bufferDocsDataRef.current = [
          ...bufferDocsDataRef.current,
          ...filteredData,
        ];
      } else {
        trainingDataRef.current = [...trainingDataRef.current, ...filteredData];
      }

      if (response.data.extractStatus === "warning") {
        dispatch(
          userSlice.actions.setToastPopUp({
            show: true,
            toastType: "error",
            message: response.data.extractMsg,
          }),
        );
      }
    } catch (err: any) {
      if (bufferFlag) bufferDocsDataRef.current.failed = true;
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: err.response?.data?.message ?? err.message,
        }),
      );
    }
  };

  const extractTextFromVideo = async (videoFiles: File[]) => {
    try {
      if (!videoFiles.length || !handleVideoUpload) return;

      const urls = await handleVideoUpload(videoFiles);
      const cleanUrls = urls.map((url) => {
        const urlObj = new URL(url);
        urlObj.search = "";
        return urlObj.toString();
      });

      const videos = cleanUrls.map((url, i) => ({
        preSignedUrl: url,
        mime: "video/mp4",
        title: videoFiles[i].name.trim(),
        size: videoFiles[i].size,
      }));

      if (bufferDocsDataRef.current.failed) {
        resetBufferData();
      }

      const res = await axiosPost("/ai/getVideoDesc", videos);
      trainingDataRef.current = [
        ...trainingDataRef.current,
        ...(res.data?.data ?? []),
      ];
    } catch (err) {
      updateGlobalDataSources(bufferDocsDataRef.current, "documents");
      resetBufferData();
      console.warn(err);
    }
  };

  const handleFileUpload = async (event: any) => {
    trainingDataRef.current = [];
    const fileData = Array.from<File>(event.target.files);
    if (!fileData.length) return;

    const extractedFile = fileData;
    const sizeLabel = plan?.ai_currentLimits?.fileSize
      ? (plan.ai_currentLimits.fileSize / (1024 * 1024)).toFixed(2)
      : 0;

    const isExisting = extractedFile.some(
      (file) =>
        store.botData.dataSources?.documents?.some(
          (doc) =>
            (doc.title ?? doc.name) === file.name &&
            (doc.size === undefined || doc.size === file.size),
        ) ||
        store.botData.aiSource?.documents?.some(
          (doc) =>
            (doc.title ?? doc.name) === file.name &&
            (doc.size === undefined || doc.size === file.size),
        ),
    );

    if (isExisting) {
      event.target.value = "";
      return dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            "Selected file is already uploaded. Please upload a different file",
        }),
      );
    }

    if (extractedFile.length > 50) {
      event.target.value = "";
      return dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            "Maximum files count exceeded. Please below upload 50 number of files",
        }),
      );
    }

    const validatedFiles = extractedFile.filter(
      (file) =>
        file.name.toLowerCase().slice(-3) === "pdf" ||
        file.name.toLowerCase().slice(-4) === "docx" ||
        file.name.toLowerCase().slice(-3) === "txt" ||
        file.name.toLowerCase().slice(-3) === "csv" ||
        file.name.toLowerCase().slice(-2) === "md" ||
        file.name.toLowerCase().slice(-4) === "xlsx",
    );

    if (validatedFiles.length !== extractedFile.length) {
      event.target.value = "";
      return dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            "File type that you are trying to upload is not supported by our system. Please try uploading a PDF, TXT or .DOC file",
        }),
      );
    }

    const validatedFileSize = extractedFile.filter(
      (file) => file.size <= plan?.ai_currentLimits?.fileSize,
    );

    if (validatedFileSize.length !== extractedFile.length) {
      event.target.value = "";
      return dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: `Maximum file size exceeded. Please upload files less than ${sizeLabel} MB`,
        }),
      );
    }

    const pdfFiles = extractedFile.filter(
      (file) =>
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf"),
    );
    const nonPdfXlsxFiles = extractedFile.filter(
      (file) =>
        file.type !== "application/pdf" &&
        !file.name.toLowerCase().endsWith(".pdf"),
    );
    const videoFiles = extractedFile.filter(
      (file) => file.name.toLowerCase().slice(-3) === "mp4",
    );
    const docExtractedFile = nonPdfXlsxFiles.filter(
      (file) => file.name.toLowerCase().slice(-3) !== "mp4",
    );

    setShowProgress(true);
    setUploadProgress(1);

    const timerInterval = setInterval(() => {
      setUploadProgress((currentProgress) => {
        let increment = 0;
        if (currentProgress < 40) increment = 2;
        else if (currentProgress < 70) increment = 1;
        else if (currentProgress < 90) increment = 1;
        else increment = 0.5;

        return Math.min(currentProgress + increment, 99);
      });
    }, 7000);

    try {
      if (docExtractedFile.length > 0) {
        const docFileData = docExtractedFile.map((file) => {
          const extension = file.name.split(".").pop()?.toUpperCase() ?? "";
          return {
            id: nanoid(),
            title: file.name,
            name: file.name,
            size: file.size,
            type: "document",
            file,
            text: `${extension} file: ${file.name}`,
            characterCount: 0,
            isDoc: true,
            mime: file.type,
          };
        });

        trainingDataRef.current = [...trainingDataRef.current, ...docFileData];
      }

      await extractTextFromVideo(videoFiles);

      if (pdfFiles.length > 0) {
        const pdfFileData = pdfFiles.map((file) => ({
          id: nanoid(),
          title: file.name,
          name: file.name,
          size: file.size,
          type: "pdf",
          file,
          text: `PDF file: ${file.name}`,
          characterCount: 0,
          isPdf: true,
          mime: "application/pdf",
        }));

        trainingDataRef.current = [...trainingDataRef.current, ...pdfFileData];
      }

      updateGlobalDataSources(trainingDataRef.current, "documents");
      clearInterval(timerInterval);
      setUploadProgress(100);
      setTimeout(() => {
        setShowProgress(false);
        setFileLoader(false);
      }, 300);
    } catch (err) {
      console.error("Error in processing:", err);
      clearInterval(timerInterval);
      setShowProgress(false);
      setFileLoader(false);
    }

    event.target.value = "";
  };

  const handleFileRemove = (index: number) => {
    const updatedFiles = store.botData.dataSources.documents.filter(
      (_, i) => i !== index,
    );
    const filesToBeRemove = store.botData.dataSources.documents.filter(
      (_, i) => i === index,
    );
    setFileUsed?.(
      (prev) =>
        prev - filesToBeRemove?.reduce((acc, obj) => acc + obj.size, 0) || 0,
    );
    storeDispatch({
      type: "data-sources-update",
      dataSources: { documents: updatedFiles },
    });
  };

  const handleDocumentTrain = async () => {
    try {
      const docData = store.botData.dataSources.documents;

      const pdfFiles = docData.filter((item) => item.isPdf && item.file);
      const xlsxFiles = docData.filter((item) => item.isXlsx && item.file);
      // Non-PDF documents now take the S3 → train-doc lambda route, so they are
      // uploaded here rather than extracted inline and sent to /ai/train.
      const asyncDocFiles = docData.filter((item) => item.isDoc && item.file);
      const nonPdfXlsxFiles = docData.filter(
        (item) => !item.isPdf && !item.isXlsx && !item.isDoc,
      );
      const totalChars = nonPdfXlsxFiles?.reduce(
        (acc, item) => acc + (item.text?.length || 0),
        0,
      );
      const characterRemaining = characterLimit - characterUsed;

      storeDispatch({
        type: "data-sources-update",
        dataSources: { documents: [] },
      });

      // PDFs and non-PDFs upload through two separate endpoints, one after the
      // other. Dispatching each set as it lands made the trained list render in
      // two waves — PDFs first, everything else seconds later. When both kinds
      // are present, hold the rows and commit them in a single dispatch.
      const hasBothKinds =
        (pdfFiles.length > 0 || xlsxFiles.length > 0) &&
        asyncDocFiles.length > 0;
      const pendingRows: any[] = [];

      if (docData.length) {
        setUploadStatus(
          `Uploading ${docData.length} file${docData.length > 1 ? "s" : ""}...`,
        );
      }

      if (pdfFiles.length > 0 || xlsxFiles.length > 0) {
        if (isOld && totalChars > characterRemaining) {
          dispatch(
            userSlice.actions.setToastPopUp({
              show: true,
              toastType: "error",
              message:
                "Character limit exceeded. Please remove some files or upgrade your plan.",
            }),
          );
          return;
        }

        setShowProgress(true);
        setUploadProgress(5);
        setFileLoader(true);

        try {
          const pdfFileObjects = pdfFiles
            .map((item) => item.file)
            .filter(Boolean);
          if (pdfFileObjects.length > 0) {
            const pdfResult: any = await handleFileUploadWithPresignedUrl(
              pdfFileObjects,
              "pdf",
              hasBothKinds,
              // First half of the bar when docs follow; the full sweep alone.
              hasBothKinds ? { from: 10, to: 50 } : { from: 30, to: 80 },
            );
            if (hasBothKinds) pendingRows.push(...(pdfResult.rows ?? []));
            if (pdfResult.failed.length > 0) {
              const failedNames = pdfResult.failed
                .map((item: any) => item.name)
                .join(", ");
              dispatch(
                userSlice.actions.setToastPopUp({
                  show: true,
                  toastType: "error",
                  message: `Couldn't upload ${pdfResult.failed.length} file(s): ${failedNames}`,
                }),
              );
            }
          }
        } catch (error) {
          console.error("Error processing PDFs/XLSX files:", error);
        } finally {
          // Keep the loader up when non-PDFs are still queued behind this —
          // hiding it here made the upload box reappear mid-run, as if
          // everything had finished while the rest were still uploading.
          if (!hasBothKinds) {
            setShowProgress(false);
            setFileLoader(false);
          }
        }
      }

      // Non-PDF documents: straight to S3, then the train-doc lambda handles
      // extraction, chunking, Pinecone upsert and credit deduction.
      if (asyncDocFiles.length > 0) {
        const requiredCredits = 0.402 * asyncDocFiles.length;

        if (!isOld && requiredCredits > Number(characterRemaining)) {
          dispatch(
            userSlice.actions.setToastPopUp({
              show: true,
              toastType: "error",
              message:
                "Character limit exceeded. Please remove some files or upgrade your plan.",
            }),
          );
          // Bailing here skips the shared cleanup below, and the PDF batch was
          // told to leave the loader up — so release it before returning.
          if (hasBothKinds) {
            setShowProgress(false);
            setFileLoader(false);
          }
          return;
        }

        try {
          const docFileObjects = asyncDocFiles
            .map((item) => item.file)
            .filter(Boolean);

          if (docFileObjects.length > 0) {
            const docResult: any = await handleDocumentUploadWithPresignedUrl(
              docFileObjects,
              hasBothKinds,
              // Picks up where the PDF batch stopped, so the bar only climbs.
              hasBothKinds ? { from: 50, to: 95 } : { from: 30, to: 80 },
            );
            if (hasBothKinds) pendingRows.push(...(docResult.rows ?? []));
          }
        } catch (error) {
          console.error("Error uploading documents:", error);
          dispatch(
            userSlice.actions.setToastPopUp({
              show: true,
              toastType: "error",
              message: "Error uploading documents. Please try again.",
            }),
          );
        }
      }

      // Both upload paths are done — commit every row at once so the trained
      // list appears complete rather than filling in piecemeal, and only now
      // drop the loader the PDF batch was told to leave up.
      if (hasBothKinds) {
        if (pendingRows.length > 0) {
          storeDispatch({
            type: "add-aiSource",
            dataKey: "documents",
            dataSet: pendingRows,
          });
          restartPolling?.();
        }

        setUploadStatus("Finishing up...");
        setUploadProgress(100);
        setTimeout(() => {
          setShowProgress(false);
          setFileLoader(false);
          setUploadStatus("");
        }, 300);
      }

      if (nonPdfXlsxFiles.length > 0) {
        const costPerFile = 0.402;
        const requiredCredits =
          Number(costPerFile) * Number(nonPdfXlsxFiles.length);
        const remaining = Number(characterRemaining);

        if (isOld && totalChars > characterRemaining) {
          dispatch(
            userSlice.actions.setToastPopUp({
              show: true,
              toastType: "error",
              message:
                "Character limit exceeded. Please remove some files or upgrade your plan.",
            }),
          );
          return;
        } else if (!isOld && requiredCredits > remaining) {
          dispatch(
            userSlice.actions.setToastPopUp({
              show: true,
              toastType: "error",
              message:
                "Character limit exceeded. Please remove some files or upgrade your plan.",
            }),
          );
          return;
        }

        storeDispatch({
          type: "add-aiSource",
          dataKey: "documents",
          dataSet: nonPdfXlsxFiles,
        });

        try {
          await axiosPost("/ai/train", {
            dataset: { documents: nonPdfXlsxFiles },
            botCustomId: store.botData.botCustomId,
            botCreatedAt: store.botData.createdAt,
          });
        } catch (error) {
          console.error("Error training non-PDF documents:", error);
          dispatch(
            userSlice.actions.setToastPopUp({
              show: true,
              toastType: "error",
              message: "Error training non-PDF documents. Please try again.",
            }),
          );
        }
      }
    } catch (error) {
      console.error("Error in handleDocumentTrain:", error);
      setShowProgress(false);
      setFileLoader(false);

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Error training documents. Please try again.",
        }),
      );
    }
  };

  return {
    fileLoader,
    handleDocumentTrain,
    handleFileRemove,
    handleFileUpload,
    showProgress,
    uploadProgress,
    uploadStatus,
  };
}
